import React, { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import AdminImageUpload from '@/components/feature/AdminImageUpload';
import { defaultCTAData } from '@/pages/home/components/CTASection';
import { defaultAboutContentES, defaultAboutContentEN } from '@/pages/about/page';
import { defaultServicesContentES, defaultServicesContentEN } from '@/pages/services/page';
import { defaultConsultingContentES, defaultConsultingContentEN } from '@/pages/business-consulting/page';
import { 
  FileText, 
  Save, 
  ExternalLink, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  Sparkles, 
  Briefcase, 
  Layers, 
  Mail,
  Loader2,
  Globe
} from 'lucide-react';

export const CMSManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'about' | 'services' | 'consulting' | 'contact'>('about');
  const [cmsLang, setCmsLang] = useState<'es' | 'en'>('es');
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  // 1. About Page Content State
  const [aboutData, setAboutData] = useState<any>({
    hero: {
      badge: 'Nuestra Historia',
      titlePrefix: 'Sobre ',
      titleHighlight: 'Nosotros',
      subtitle: 'Conoce la historia detrás de Smart Business Digital Solutions y la misión que nos impulsa a empoderar emprendedores latinos en todo el mundo.'
    },
    story: {
      photoBadge: 'Fundadora & CEO',
      badge: 'Mi Historia',
      title: 'De Emprendedora a',
      titleHighlight: 'Mentora Digital',
      name: 'Cathy Roldán',
      company: 'Smart Business Digital Solutions LLC',
      photoUrl: 'https://readdy.ai/api/search-image?query=confident%20latina%20entrepreneur%20businesswoman%20digital%20mentor%20professional%20photo%20studio%20purple%20blue%20lighting%20elegant%20modern%20background%20empowering&width=500&height=600&seq=cathy2&orientation=portrait',
      p1: 'Cathy Roldán es una emprendedora, mentora digital y educadora apasionada por empoderar a la comunidad latina. Con raíces profundas en la cultura hispana, Cathy entendió desde temprano que el acceso a la educación digital podía cambiar vidas.',
      p2: 'Comenzó su camino en el marketing digital de forma autodidacta, aprendiendo cada herramienta, cada estrategia y cada plataforma disponible. Lo que empezó como una necesidad personal se convirtió en una misión: democratizar el conocimiento digital para emprendedores latinos.',
      p3: 'Hoy, a través de Smart Business Digital Solutions LLC y Smart Business Academy, Cathy ha ayudado a más de 500 estudiantes y 200 clientes a transformar sus negocios, aumentar sus ingresos y construir marcas poderosas en el mundo digital.',
      p4: 'Su enfoque combina estrategia de marketing digital, herramientas de inteligencia artificial, automatización de negocios y educación práctica. Todo diseñado para que cualquier emprendedor, sin importar su nivel técnico, pueda dominar el mundo online.',
      trajectoryTitle: 'Trayectoria',
      milestones: [
        { year: '2018', title: 'El Comienzo', desc: 'Cathy inicia su carrera en marketing digital, aprendiendo de forma autodidacta las estrategias de redes sociales y negocios online.' },
        { year: '2019', title: 'Primeros Clientes', desc: 'Comienza a trabajar con pequeños negocios latinos, ayudándoles a establecer su presencia digital y aumentar sus ventas.' },
        { year: '2021', title: 'Smart Business Nace', desc: 'Funda Smart Business Digital Solutions LLC, consolidando su equipo y expandiendo los servicios de marketing digital y automatización.' },
        { year: '2022', title: 'Smart Business Academy', desc: 'Lanza la academia online para emprendedores latinos, democratizando el acceso a educación digital de calidad.' },
        { year: '2024', title: 'Impacto Global', desc: 'Más de 500 estudiantes y 200 clientes en toda Latinoamérica y Estados Unidos, con presencia en múltiples plataformas digitales.' }
      ]
    },
    missionVision: {
      mission: {
        label: 'Misión',
        title: 'Empoderar Emprendedores Latinos',
        text: 'Nuestra misión es empoderar a emprendedores y dueños de negocios latinos en todo el mundo, brindándoles las herramientas, estrategias y conocimientos de marketing digital e inteligencia artificial necesarios para construir negocios rentables y sostenibles en la era digital.'
      },
      vision: {
        label: 'Visión',
        title: 'Líderes del Mundo Digital Latino',
        text: 'Ser la empresa de marketing digital y educación online más influyente para la comunidad latina, creando un ecosistema donde cada emprendedor hispano tenga acceso a las mismas oportunidades digitales que los líderes globales del negocio.'
      },
      valuesTitle: 'Nuestros Valores',
      valuesSub: 'Lo Que Nos',
      valuesHighlight: 'Define',
      values: [
        { icon: 'ri-heart-line', title: 'Comunidad', desc: 'Creemos en el poder de la comunidad latina y trabajamos para fortalecer cada negocio que forma parte de ella.', color: '#FF2DAA' },
        { icon: 'ri-lightbulb-line', title: 'Innovación', desc: 'Adoptamos las últimas tecnologías de IA y marketing digital para mantener a nuestros clientes a la vanguardia.', color: '#8c52ff' },
        { icon: 'ri-shield-check-line', title: 'Integridad', desc: 'Trabajamos con transparencia y honestidad, priorizando siempre el éxito real de nuestros clientes.', color: '#00c2ff' },
        { icon: 'ri-rocket-line', title: 'Resultados', desc: 'Nos enfocamos en métricas reales: más seguidores, más ventas, más ingresos para tu negocio.', color: '#FF2DAA' }
      ],
      experienceTitle: 'Experiencia & Especialidades',
      areas: [
        { area: 'Marketing Digital', skills: ['Social Media Strategy', 'Content Marketing', 'SEO & SEM', 'Paid Advertising', 'Analytics'], color: '#8c52ff' },
        { area: 'Tecnología & IA', skills: ['AI Automation Tools', 'Sales Funnels', 'Email Marketing', 'CRM Systems', 'Chatbots'], color: '#00c2ff' },
        { area: 'Educación & Coaching', skills: ['Online Courses', 'Group Coaching', '1:1 Mentoring', 'Workshops', 'Community Building'], color: '#FF2DAA' }
      ]
    },
    cta: defaultCTAData
  });

  // 2. Services Page Content State
  const [servicesData, setServicesData] = useState<any>({
    hero: {
      badge: 'Soluciones Digitales',
      titlePrefix: 'Todo lo que tu Negocio Necesita',
      titleHighlight: 'para Crecer Online',
      subtitle: 'Desde sitios web personalizados y branding hasta automatización con IA y estrategia — construimos la infraestructura digital que convierte tu visión en resultados.',
      ctaBook: 'Agendar una Consultoría Gratuita',
      ctaTeam: 'Conoce al Equipo',
      stats: [
        { value: '9+', label: 'Servicios Ofrecidos' },
        { value: '100%', label: 'Soluciones Personalizadas' },
        { value: '24/7', label: 'Soporte y Hosting' }
      ]
    },
    detail: {
      featured: {
        badge: 'Servicio Destacado',
        mostPopular: 'Más Popular',
        title: 'Creación de Sitio Web + Hosting',
        color: '#00c2ff',
        accentColor: '#8c52ff',
        image: 'https://readdy.ai/api/search-image?query=modern%20professional%20website%20design%20laptop%20screen%20glowing%20neon%20light%20dark%20background%20custom%20web%20development%20high%20converting%20landing%20page%20clean%20minimal%20elegant%20digital%20agency%20studio&width=1200&height=600&seq=feat1&orientation=landscape',
        desc: 'Sitios web personalizados, modernos y de alta conversión diseñados para empresas y emprendedores. Incluye desarrollo completo, optimización móvil, estructura lista para SEO e integración de hosting.',
        benefits: [
          'Diseño personalizado adaptado a tu marca',
          'Optimizado para móviles y totalmente responsivo',
          'Estructura lista para SEO desde el primer día',
          'Rendimiento rápido y hosting incluido',
          'Formularios de captura e integración de CTAs',
          'Solución integral — sin dolores de cabeza técnicos'
        ],
        forWho: 'Dueños de negocios, emprendedores y marcas que desean una presencia en línea sólida, profesional y escalable.',
        buttonText: 'Reservar una Consulta',
        buttonLink: '/contact'
      },
      header: {
        prefix: 'Todos Nuestros',
        highlight: 'Servicios',
        subtitle: 'Todo lo que necesitas para construir, hacer crecer y escalar tu negocio en línea — bajo un mismo techo.'
      },
      items: [
        {
          icon: 'ri-customer-service-2-line',
          title: 'Creación de CRM',
          color: '#FF2DAA',
          image: 'https://readdy.ai/api/search-image?query=CRM%20dashboard%20client%20management%20system%20modern%20dark%20UI%20neon%20pink%20purple%20glow%20leads%20pipeline%20workflow%20automation%20professional%20software%20interface&width=700&height=450&seq=crm1&orientation=landscape',
          desc: 'Sistemas CRM hechos a medida para gestionar leads, clientes, flujos de trabajo y operaciones internas de manera eficiente.',
          benefits: ['Mejor organización y seguimiento de clientes', 'Automatización de procesos repetitivos', 'Aumento de la productividad del equipo', 'Datos e informes centralizados'],
          forWho: 'Empresas que desean escalar sus operaciones.',
          buttonText: 'Reservar una Consulta',
          buttonLink: '/contact'
        },
        {
          icon: 'ri-palette-line',
          title: 'Branding y Diseño',
          color: '#8c52ff',
          image: 'https://readdy.ai/api/search-image?query=brand%20identity%20design%20logo%20color%20palette%20typography%20visual%20direction%20modern%20elegant%20dark%20background%20purple%20gradient%20creative%20agency%20professional%20branding%20kit&width=700&height=450&seq=brand1&orientation=landscape',
          desc: 'Creación de identidad de marca, incluyendo logotipos, paletas de colores, dirección visual y materiales de marketing.',
          benefits: ['Mayor reconocimiento de marca', 'Imagen profesional y consistente', 'Logo + kit de marca completo', 'Plantillas de material de marketing'],
          forWho: 'Nuevos negocios que necesitan un refresh o una identidad más sólida.',
          buttonText: 'Reservar una Consulta',
          buttonLink: '/contact'
        }
      ],
      footerCta: {
        title: '¿No estás seguro de qué servicio es el adecuado para ti?',
        subtitle: 'Agenda una llamada estratégica gratuita y te ayudaremos a determinar exactamente lo que tu negocio necesita para crecer.',
        book: 'Obtener una Consultoría Gratuita',
        link: '/contact'
      }
    },
    cta: defaultCTAData
  });

  // 3. Consulting Page Content State
  const [consultingData, setConsultingData] = useState<any>({
    hero: {
      badge: 'Consultoría de Negocios',
      titlePrefix: 'Construye tu Negocio ',
      titleHighlight: 'de la Manera Correcta',
      subtitle: 'Desde la formación de LLC y licencias hasta la estructura y estrategia empresarial — guiamos a emprendedores en cada paso.',
      ctaBook: 'Reservar una Consulta',
      ctaExplore: 'Explorar Servicios',
      bgImage: 'https://readdy.ai/api/search-image?query=professional%20business%20consulting%20meeting%20boardroom%20dark%20elegant%20atmosphere%20two%20entrepreneurs%20discussing%20strategy%20documents%20laptop%20modern%20office%20neon%20ambient%20lighting%20sophisticated%20corporate%20environment&width=1600&height=900&seq=bchero1&orientation=landscape',
      stats: [
        { value: 'LLC', label: 'Soporte de Formación' },
        { value: '100%', label: 'Guía Personalizada' },
        { value: 'Setup', label: 'Configuración de Negocio' }
      ]
    },
    services: {
      badge: 'Lo que Ofrecemos',
      titlePrefix: 'Servicios de ',
      titleHighlight: 'Consultoría Empresarial',
      subtitle: 'Orientación integral para ayudarte a construir, estructurar y hacer crecer un negocio legítimo y escalable.',
      items: [
        {
          icon: 'ri-building-2-line',
          title: 'Creación de Negocio y Formación de LLC',
          color: '#8c52ff',
          image: 'https://readdy.ai/api/search-image?query=corporate%20legal%20documents%20LLC%20formation%20certificate%20fountain%20pen%20dark%20wood%20desk%20laptop%20modern%20office%20ambient%20neon%20lighting%20professional%20business%20setup&width=700&height=450&seq=llc1&orientation=landscape',
          desc: 'Te guiamos en el proceso de establecer tu entidad comercial — desde elegir la estructura hasta formar una LLC.',
          benefits: ['Guía sobre LLC vs otras estructuras', 'Guía de formación paso a paso', 'Acuerdos operativos', 'Guía para EIN y cuentas bancarias', 'Conexión con red profesional', 'Empezar de la manera correcta'],
          forWho: 'Emprendedores listos para formalizar.',
          buttonText: 'Reservar una Consulta',
          buttonLink: '/contact'
        }
      ]
    },
    whoFor: {
      badge: 'Para Quién Es',
      titlePrefix: '¿Es Esto ',
      titleHighlight: 'Para Ti?',
      subtitle: 'Nuestros servicios de consultoría están diseñados para personas decididas y listas para construir algo real.',
      profiles: [
        { icon: 'ri-user-star-line', color: '#8c52ff', title: 'Emprendedores Primerizos', desc: 'Tienes una gran idea pero no sabes por dónde empezar. Te ayudamos a construir la base desde el primer día.' }
      ]
    },
    process: {
      badge: 'El Proceso',
      titlePrefix: 'Cómo ',
      titleHighlight: 'Funciona',
      subtitle: 'Un proceso simple diseñado para poner tu negocio en marcha — rápido.',
      steps: [
        { icon: 'ri-calendar-check-line', color: '#8c52ff', step: '01', title: 'Reservar una Consulta', desc: 'Agenda una sesión estratégica gratuita para hablar sobre tus metas.' }
      ],
      cta: {
        titlePrefix: '¿Listo para Construir tu Negocio ',
        titleHighlight: 'de la Manera Correcta?',
        subtitle: 'Deja de adivinar y empieza a construir con confianza. Reserva una consulta gratuita hoy mismo.',
        ctaBook: 'Reservar una Consulta Gratis',
        ctaBookLink: '/contact',
        ctaServices: 'Ver Todos los Servicios',
        ctaServicesLink: '/services'
      },
      disclaimer: {
        title: 'Aviso Legal',
        text: 'No ofrecemos asesoramiento legal ni financiero. Todas las decisiones deben tomarse en consulta con un profesional licenciado.'
      }
    }
  });

  // 4. Contact Page Content State
  const [contactData, setContactData] = useState<any>({
    email: 'info@smartbusinessdigital.com',
    response: { label: 'Tiempo de Respuesta', value: '24-48 Horas', days: 'Días laborables' },
    service: { label: 'Área de Servicio', value: 'Global', languages: 'Español e Inglés' },
    socialLinks: [
      { icon: 'ri-instagram-line', label: 'Instagram', handle: '@smartbusinessdigital', href: 'https://instagram.com', color: '#FF2DAA' },
      { icon: 'ri-facebook-circle-line', label: 'Facebook', handle: 'Smart Business Digital', href: 'https://facebook.com', color: '#8c52ff' },
      { icon: 'ri-tiktok-line', label: 'TikTok', handle: '@smartbusinessdigital', href: 'https://tiktok.com', color: '#00c2ff' },
    ],
    calendar: {
      badge: 'AGENDA UNA LLAMADA',
      titlePrefix: 'Reserva una',
      titleHighlight: 'Asesoría Gratuita',
      subtitle: 'Hablemos de tus objetivos y cómo podemos ayudarte a escalar tu negocio.',
      cta: 'Reservar Llamada',
      link: 'https://stan.store'
    }
  });

  useEffect(() => {
    const loadAllPages = async () => {
      setLoadingContent(true);
      const defaultAbout = cmsLang === 'en' ? defaultAboutContentEN : defaultAboutContentES;
      const defaultServices = cmsLang === 'en' ? defaultServicesContentEN : defaultServicesContentES;
      const defaultConsulting = cmsLang === 'en' ? defaultConsultingContentEN : defaultConsultingContentES;

      setAboutData(defaultAbout);
      setServicesData(defaultServices);
      setConsultingData(defaultConsulting);

      const aboutDocId = cmsLang === 'en' ? 'about_page_en' : 'about_page';
      const servicesDocId = cmsLang === 'en' ? 'services_page_en' : 'services_page';
      const consultingDocId = cmsLang === 'en' ? 'consulting_page_en' : 'consulting_page';
      const contactDocId = cmsLang === 'en' ? 'contact_en' : 'contact';

      try {
        // Load About
        const aboutSnap = await getDoc(doc(db, 'landing_content', aboutDocId));
        if (aboutSnap.exists()) {
          const raw = aboutSnap.data();
          setAboutData({
            hero: { ...defaultAbout.hero, ...(raw.hero || {}) },
            story: {
              ...defaultAbout.story,
              ...(raw.story || {}),
              badge: raw.story?.badge || defaultAbout.story.badge,
              title: raw.story?.title || defaultAbout.story.title,
              titleHighlight: raw.story?.titleHighlight || defaultAbout.story.titleHighlight,
              p1: raw.story?.p1 || defaultAbout.story.p1,
              p2: raw.story?.p2 || defaultAbout.story.p2,
              p3: raw.story?.p3 || defaultAbout.story.p3,
              p4: raw.story?.p4 || defaultAbout.story.p4,
              milestones: raw.story?.milestones || defaultAbout.story.milestones,
            },
            missionVision: {
              ...defaultAbout.missionVision,
              ...(raw.missionVision || {}),
              values: raw.missionVision?.values || defaultAbout.missionVision.values,
              areas: raw.missionVision?.areas || defaultAbout.missionVision.areas,
            },
            cta: {
              ...defaultAbout.cta,
              ...(raw.cta || {}),
              trustIndicators: raw.cta?.trustIndicators || defaultAbout.cta.trustIndicators,
            },
          });
        }

        // Load Services
        const servicesSnap = await getDoc(doc(db, 'landing_content', servicesDocId));
        if (servicesSnap.exists()) {
          const raw = servicesSnap.data();
          setServicesData({
            hero: { ...defaultServices.hero, ...(raw.hero || {}), stats: raw.hero?.stats || defaultServices.hero.stats },
            detail: {
              ...defaultServices.detail,
              ...(raw.detail || {}),
              featured: { ...defaultServices.detail.featured, ...(raw.detail?.featured || {}) },
              header: { ...defaultServices.detail.header, ...(raw.detail?.header || {}) },
              items: raw.detail?.items || defaultServices.detail.items,
              footerCta: { ...defaultServices.detail.footerCta, ...(raw.detail?.footerCta || {}) },
            },
            cta: { ...defaultServices.cta, ...(raw.cta || {}) },
          });
        }

        // Load Consulting
        const consultingSnap = await getDoc(doc(db, 'landing_content', consultingDocId));
        if (consultingSnap.exists()) {
          const raw = consultingSnap.data();
          setConsultingData({
            hero: { ...defaultConsulting.hero, ...(raw.hero || {}), stats: raw.hero?.stats || defaultConsulting.hero.stats },
            services: {
              ...defaultConsulting.services,
              ...(raw.services || {}),
              items: raw.services?.items || defaultConsulting.services.items,
            },
            whoFor: {
              ...defaultConsulting.whoFor,
              ...(raw.whoFor || {}),
              profiles: raw.whoFor?.profiles || defaultConsulting.whoFor.profiles,
            },
            process: {
              ...defaultConsulting.process,
              ...(raw.process || {}),
              steps: raw.process?.steps || defaultConsulting.process.steps,
              cta: { ...defaultConsulting.process.cta, ...(raw.process?.cta || {}) },
              disclaimer: { ...defaultConsulting.process.disclaimer, ...(raw.process?.disclaimer || {}) },
            },
          });
        }

        // Load Contact
        const contactSnap = await getDoc(doc(db, 'landing_content', contactDocId));
        if (contactSnap.exists()) {
          setContactData((prev: any) => ({ ...prev, ...contactSnap.data() }));
        }
      } catch (err) {
        console.error('Error loading pages content in CMS:', err);
      } finally {
        setLoadingContent(false);
      }
    };

    loadAllPages();
  }, [cmsLang]);

  const handleSavePage = async (page: 'about' | 'services' | 'consulting' | 'contact') => {
    setSaving(true);
    const aboutDocId = cmsLang === 'en' ? 'about_page_en' : 'about_page';
    const servicesDocId = cmsLang === 'en' ? 'services_page_en' : 'services_page';
    const consultingDocId = cmsLang === 'en' ? 'consulting_page_en' : 'consulting_page';
    const contactDocId = cmsLang === 'en' ? 'contact_en' : 'contact';

    try {
      if (page === 'about') {
        await setDoc(doc(db, 'landing_content', aboutDocId), aboutData);
      } else if (page === 'services') {
        await setDoc(doc(db, 'landing_content', servicesDocId), servicesData);
      } else if (page === 'consulting') {
        await setDoc(doc(db, 'landing_content', consultingDocId), consultingData);
      } else if (page === 'contact') {
        await setDoc(doc(db, 'landing_content', contactDocId), contactData);
      }
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3500);
    } catch (err) {
      console.error('Error saving in CMS:', err);
      alert('Error al guardar cambios.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white flex items-center gap-3">
            <Layers className="text-purple-400" />
            Editor de Contenido y Páginas Web
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Modifica la información, títulos, textos, imágenes y botones de todas las páginas de tu sitio web.
          </p>
        </div>

        {savedSuccess && (
          <div className="bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
            <CheckCircle2 size={18} />
            <span>¡Información guardada exitosamente!</span>
          </div>
        )}
      </div>

      {/* Page Tabs and Language Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Page Tabs */}
        <div className="flex flex-wrap gap-2 p-1.5 bg-slate-900/80 rounded-2xl border border-slate-700/60 w-fit">
          <button
            onClick={() => setActiveTab('about')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'about'
                ? 'bg-gradient-to-r from-purple-600 to-[#FF2DAA] text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Sparkles size={16} />
            Sobre Nosotros (/about)
          </button>

          <button
            onClick={() => setActiveTab('services')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'services'
                ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Briefcase size={16} />
            Nuestros Servicios (/services)
          </button>

          <button
            onClick={() => setActiveTab('consulting')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'consulting'
                ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <FileText size={16} />
            Consultoría (/business-consulting)
          </button>

          <button
            onClick={() => setActiveTab('contact')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold flex items-center gap-2 transition-all cursor-pointer ${
              activeTab === 'contact'
                ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Mail size={16} />
            Contacto (/contact)
          </button>
        </div>

        {/* Language Switcher */}
        <div className="flex items-center gap-2 p-1.5 bg-slate-900/90 rounded-2xl border border-purple-500/40 w-fit">
          <Globe size={16} className="text-purple-400 ml-2" />
          <span className="text-xs text-purple-300 font-bold pr-1">Idioma a editar:</span>
          <button
            type="button"
            onClick={() => setCmsLang('es')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              cmsLang === 'es' ? 'bg-gradient-to-r from-purple-600 to-[#FF2DAA] text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <span>🇪🇸</span>
            <span>Español (ES)</span>
          </button>
          <button
            type="button"
            onClick={() => setCmsLang('en')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              cmsLang === 'en' ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <span>🇺🇸</span>
            <span>English (EN)</span>
          </button>
        </div>
      </div>

      {loadingContent && (
        <div className="flex items-center gap-2 text-purple-400 py-6">
          <Loader2 className="animate-spin" size={20} />
          <span>Cargando contenido de la página...</span>
        </div>
      )}

      {/* ── TAB 1: ABOUT PAGE EDITOR ── */}
      {activeTab === 'about' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-slate-800/40 p-4 rounded-xl border border-purple-500/20">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse" />
              <span className="text-white font-bold text-sm">Editando: Sobre Nosotros ({cmsLang === 'en' ? 'Inglés - EN' : 'Español - ES'})</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/about"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <ExternalLink size={14} /> Ver en Vivo
              </a>
              <button
                onClick={() => handleSavePage('about')}
                disabled={saving}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 cursor-pointer"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Guardar Sobre Nosotros ({cmsLang.toUpperCase()})
              </button>
            </div>
          </div>

          {/* Section: Hero */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-purple-300 flex items-center gap-2">
              <i className="ri-layout-top-line" /> 1. Sección Hero Principal
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Badge Superior</label>
                <input
                  type="text"
                  value={aboutData.hero.badge}
                  onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, badge: e.target.value } })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Prefijo de Título</label>
                <input
                  type="text"
                  value={aboutData.hero.titlePrefix}
                  onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, titlePrefix: e.target.value } })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="text-xs text-purple-400 font-semibold block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={aboutData.hero.titleHighlight}
                  onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, titleHighlight: e.target.value } })}
                  className="w-full bg-slate-900 border border-purple-500/50 rounded-lg px-3 py-2 text-purple-300 text-sm font-bold focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-semibold block mb-1">Subtítulo Descriptivo</label>
              <textarea
                value={aboutData.hero.subtitle}
                onChange={(e) => setAboutData({ ...aboutData, hero: { ...aboutData.hero, subtitle: e.target.value } })}
                rows={2}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Section: Cathy Story */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-pink-300 flex items-center gap-2">
              <i className="ri-user-star-line" /> 2. Historia de la Fundadora (Cathy Roldán)
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-3">
                <AdminImageUpload
                  label="Foto de Cathy Roldán"
                  value={aboutData.story?.photoUrl || ''}
                  onChange={(url) => setAboutData({ ...aboutData, story: { ...aboutData.story, photoUrl: url } })}
                  folder="about"
                />
                <div>
                  <label className="text-xs text-slate-400 font-semibold block mb-1">Nombre Completo</label>
                  <input
                    type="text"
                    value={aboutData.story?.name || ''}
                    onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, name: e.target.value } })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block mb-1">Cargo / Rol</label>
                  <input
                    type="text"
                    value={aboutData.story?.photoBadge || ''}
                    onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, photoBadge: e.target.value } })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 font-semibold block mb-1">Empresa</label>
                  <input
                    type="text"
                    value={aboutData.story?.company || ''}
                    onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, company: e.target.value } })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div className="lg:col-span-2 space-y-3">
                <div>
                  <label className="text-xs text-purple-400 font-semibold block mb-1">Badge de la Historia</label>
                  <input
                    type="text"
                    value={aboutData.story?.badge || ''}
                    onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, badge: e.target.value } })}
                    className="w-full bg-slate-900 border border-purple-500/50 rounded-lg px-3 py-2 text-purple-300 text-sm font-semibold focus:outline-none focus:border-purple-400"
                    placeholder="Mi Historia"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-semibold block mb-1">Título de la Historia</label>
                    <input
                      type="text"
                      value={aboutData.story?.title || ''}
                      onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, title: e.target.value } })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                      placeholder="De Emprendedora a"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-pink-400 font-semibold block mb-1">Título Destacado</label>
                    <input
                      type="text"
                      value={aboutData.story?.titleHighlight || ''}
                      onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, titleHighlight: e.target.value } })}
                      className="w-full bg-slate-900 border border-pink-500/50 rounded-lg px-3 py-2 text-pink-300 text-sm font-bold focus:outline-none focus:border-pink-400"
                      placeholder="Mentora Digital"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-slate-400 font-semibold block">Párrafos de la Historia</label>
                  <div>
                    <span className="text-[10px] text-slate-500 block mb-0.5">Párrafo 1</span>
                    <textarea
                      value={aboutData.story?.p1 || ''}
                      onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, p1: e.target.value } })}
                      rows={2}
                      placeholder="Párrafo 1"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block mb-0.5">Párrafo 2</span>
                    <textarea
                      value={aboutData.story?.p2 || ''}
                      onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, p2: e.target.value } })}
                      rows={2}
                      placeholder="Párrafo 2"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block mb-0.5">Párrafo 3</span>
                    <textarea
                      value={aboutData.story?.p3 || ''}
                      onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, p3: e.target.value } })}
                      rows={2}
                      placeholder="Párrafo 3"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block mb-0.5">Párrafo 4</span>
                    <textarea
                      value={aboutData.story?.p4 || ''}
                      onChange={(e) => setAboutData({ ...aboutData, story: { ...aboutData.story, p4: e.target.value } })}
                      rows={2}
                      placeholder="Párrafo 4"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Milestones in About */}
            <div className="pt-4 border-t border-slate-700/60 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <i className="ri-road-map-line text-purple-400" /> Hitos de Trayectoria
                </h4>
                <button
                  type="button"
                  onClick={() =>
                    setAboutData({
                      ...aboutData,
                      story: {
                        ...aboutData.story,
                        milestones: [
                          ...aboutData.story.milestones,
                          { year: '2025', title: 'Nuevo Hito', desc: 'Descripción del hito alcanzado.' },
                        ],
                      },
                    })
                  }
                  className="px-3 py-1 bg-purple-600/30 hover:bg-purple-600 border border-purple-500/40 text-purple-200 text-xs font-semibold rounded-lg flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <Plus size={14} /> Añadir Hito
                </button>
              </div>

              <div className="space-y-2">
                {aboutData.story.milestones.map((m: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-900/60 p-2.5 rounded-xl border border-slate-700/40">
                    <input
                      type="text"
                      value={m.year}
                      onChange={(e) => {
                        const updated = aboutData.story.milestones.map((item: any, idx: number) =>
                          idx === i ? { ...item, year: e.target.value } : item
                        );
                        setAboutData({ ...aboutData, story: { ...aboutData.story, milestones: updated } });
                      }}
                      className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-purple-300 text-xs font-bold"
                    />
                    <input
                      type="text"
                      value={m.title}
                      onChange={(e) => {
                        const updated = aboutData.story.milestones.map((item: any, idx: number) =>
                          idx === i ? { ...item, title: e.target.value } : item
                        );
                        setAboutData({ ...aboutData, story: { ...aboutData.story, milestones: updated } });
                      }}
                      className="w-48 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-xs font-bold"
                      placeholder="Título del hito"
                    />
                    <input
                      type="text"
                      value={m.desc}
                      onChange={(e) => {
                        const updated = aboutData.story.milestones.map((item: any, idx: number) =>
                          idx === i ? { ...item, desc: e.target.value } : item
                        );
                        setAboutData({ ...aboutData, story: { ...aboutData.story, milestones: updated } });
                      }}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white/70 text-xs"
                      placeholder="Descripción"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const updated = aboutData.story.milestones.filter((_: any, idx: number) => idx !== i);
                        setAboutData({ ...aboutData, story: { ...aboutData.story, milestones: updated } });
                      }}
                      className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section: Mission & Vision */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-cyan-300 flex items-center gap-2">
              <i className="ri-compass-3-line" /> 3. Misión y Visión
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-900/60 p-4 rounded-xl border border-purple-500/30 space-y-2">
                <label className="text-xs text-purple-400 font-bold block">Misión</label>
                <input
                  type="text"
                  value={aboutData.missionVision.mission.title}
                  onChange={(e) =>
                    setAboutData({
                      ...aboutData,
                      missionVision: {
                        ...aboutData.missionVision,
                        mission: { ...aboutData.missionVision.mission, title: e.target.value },
                      },
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-white font-bold text-sm"
                />
                <textarea
                  value={aboutData.missionVision.mission.text}
                  onChange={(e) =>
                    setAboutData({
                      ...aboutData,
                      missionVision: {
                        ...aboutData.missionVision,
                        mission: { ...aboutData.missionVision.mission, text: e.target.value },
                      },
                    })
                  }
                  rows={4}
                  className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white/80 text-xs"
                />
              </div>

              <div className="bg-slate-900/60 p-4 rounded-xl border border-cyan-500/30 space-y-2">
                <label className="text-xs text-cyan-400 font-bold block">Visión</label>
                <input
                  type="text"
                  value={aboutData.missionVision.vision.title}
                  onChange={(e) =>
                    setAboutData({
                      ...aboutData,
                      missionVision: {
                        ...aboutData.missionVision,
                        vision: { ...aboutData.missionVision.vision, title: e.target.value },
                      },
                    })
                  }
                  className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-white font-bold text-sm"
                />
                <textarea
                  value={aboutData.missionVision.vision.text}
                  onChange={(e) =>
                    setAboutData({
                      ...aboutData,
                      missionVision: {
                        ...aboutData.missionVision,
                        vision: { ...aboutData.missionVision.vision, text: e.target.value },
                      },
                    })
                  }
                  rows={4}
                  className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white/80 text-xs"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => handleSavePage('about')}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-[#FF2DAA] hover:opacity-90 disabled:opacity-50 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-purple-600/30 cursor-pointer"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Guardar Cambios de Sobre Nosotros ({cmsLang.toUpperCase()})
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 2: SERVICES PAGE EDITOR ── */}
      {activeTab === 'services' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-slate-800/40 p-4 rounded-xl border border-cyan-500/20">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-white font-bold text-sm">Editando: Nuestros Servicios ({cmsLang === 'en' ? 'Inglés - EN' : 'Español - ES'})</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/services"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <ExternalLink size={14} /> Ver en Vivo
              </a>
              <button
                onClick={() => handleSavePage('services')}
                disabled={saving}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 cursor-pointer"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Guardar Servicios ({cmsLang.toUpperCase()})
              </button>
            </div>
          </div>

          {/* Hero Services */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-cyan-300">1. Hero de Servicios</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Badge</label>
                <input
                  type="text"
                  value={servicesData.hero.badge}
                  onChange={(e) => setServicesData({ ...servicesData, hero: { ...servicesData.hero, badge: e.target.value } })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Prefijo de Título</label>
                <input
                  type="text"
                  value={servicesData.hero.titlePrefix}
                  onChange={(e) => setServicesData({ ...servicesData, hero: { ...servicesData.hero, titlePrefix: e.target.value } })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-cyan-400 font-semibold block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={servicesData.hero.titleHighlight}
                  onChange={(e) => setServicesData({ ...servicesData, hero: { ...servicesData.hero, titleHighlight: e.target.value } })}
                  className="w-full bg-slate-900 border border-cyan-500/50 rounded-lg px-3 py-2 text-cyan-300 text-sm font-bold"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-semibold block mb-1">Subtítulo</label>
              <textarea
                value={servicesData.hero.subtitle}
                onChange={(e) => setServicesData({ ...servicesData, hero: { ...servicesData.hero, subtitle: e.target.value } })}
                rows={2}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>

          {/* Featured Service */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-cyan-300">2. Servicio Destacado (Sitio Web + Hosting)</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <AdminImageUpload
                  label="Imagen de Servicio Destacado"
                  value={servicesData.detail.featured.image}
                  onChange={(url) =>
                    setServicesData({
                      ...servicesData,
                      detail: {
                        ...servicesData.detail,
                        featured: { ...servicesData.detail.featured, image: url },
                      },
                    })
                  }
                  folder="services"
                />
              </div>
              <div className="lg:col-span-2 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-semibold block mb-1">Título</label>
                    <input
                      type="text"
                      value={servicesData.detail.featured.title}
                      onChange={(e) =>
                        setServicesData({
                          ...servicesData,
                          detail: {
                            ...servicesData.detail,
                            featured: { ...servicesData.detail.featured, title: e.target.value },
                          },
                        })
                      }
                      className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-cyan-400 font-semibold block mb-1">Badge Más Popular</label>
                    <input
                      type="text"
                      value={servicesData.detail.featured.mostPopular}
                      onChange={(e) =>
                        setServicesData({
                          ...servicesData,
                          detail: {
                            ...servicesData.detail,
                            featured: { ...servicesData.detail.featured, mostPopular: e.target.value },
                          },
                        })
                      }
                      className="w-full bg-slate-900 border border-cyan-500/40 rounded px-3 py-2 text-cyan-300 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-semibold block mb-1">Descripción</label>
                  <textarea
                    value={servicesData.detail.featured.desc}
                    onChange={(e) =>
                      setServicesData({
                        ...servicesData,
                        detail: {
                          ...servicesData.detail,
                          featured: { ...servicesData.detail.featured, desc: e.target.value },
                        },
                      })
                    }
                    rows={2}
                    className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white text-xs"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-semibold block mb-1">Para quién es</label>
                  <input
                    type="text"
                    value={servicesData.detail.featured.forWho}
                    onChange={(e) =>
                      setServicesData({
                        ...servicesData,
                        detail: {
                          ...servicesData.detail,
                          featured: { ...servicesData.detail.featured, forWho: e.target.value },
                        },
                      })
                    }
                    className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-white/80 text-xs"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Catalog of Services with ADD / DELETE */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <i className="ri-list-check text-cyan-400" /> 3. Catálogo de Servicios ({servicesData.detail.items.length})
              </h3>
              <button
                type="button"
                onClick={() => {
                  const newService = {
                    icon: 'ri-sparkling-fill',
                    title: 'Nuevo Servicio Digital',
                    color: '#8c52ff',
                    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&h=450&fit=crop',
                    desc: 'Descripción del servicio.',
                    benefits: ['Beneficio 1', 'Beneficio 2', 'Beneficio 3'],
                    forWho: 'Empresas y marcas en crecimiento.',
                    buttonText: 'Reservar una Consulta',
                    buttonLink: '/contact',
                  };
                  setServicesData({
                    ...servicesData,
                    detail: {
                      ...servicesData.detail,
                      items: [...servicesData.detail.items, newService],
                    },
                  });
                }}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-cyan-600/30"
              >
                <Plus size={16} /> Añadir Nuevo Servicio
              </button>
            </div>

            <div className="space-y-4">
              {servicesData.detail.items.map((svc: any, i: number) => (
                <div key={i} className="bg-slate-900/60 p-4 rounded-xl border border-slate-700/60 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">
                      Servicio #{i + 1}: {svc.title}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = servicesData.detail.items.filter((_: any, idx: number) => idx !== i);
                        setServicesData({
                          ...servicesData,
                          detail: { ...servicesData.detail, items: updated },
                        });
                      }}
                      className="px-2.5 py-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg text-xs font-medium flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 size={12} /> Eliminar
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] text-slate-400 uppercase block mb-1">Título</label>
                      <input
                        type="text"
                        value={svc.title}
                        onChange={(e) => {
                          const updated = servicesData.detail.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, title: e.target.value } : item
                          );
                          setServicesData({ ...servicesData, detail: { ...servicesData.detail, items: updated } });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400 uppercase block mb-1">Color HEX</label>
                      <input
                        type="text"
                        value={svc.color}
                        onChange={(e) => {
                          const updated = servicesData.detail.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, color: e.target.value } : item
                          );
                          setServicesData({ ...servicesData, detail: { ...servicesData.detail, items: updated } });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400 uppercase block mb-1">Para quién es</label>
                      <input
                        type="text"
                        value={svc.forWho}
                        onChange={(e) => {
                          const updated = servicesData.detail.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, forWho: e.target.value } : item
                          );
                          setServicesData({ ...servicesData, detail: { ...servicesData.detail, items: updated } });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 uppercase block mb-1">Descripción</label>
                    <textarea
                      value={svc.desc}
                      onChange={(e) => {
                        const updated = servicesData.detail.items.map((item: any, idx: number) =>
                          idx === i ? { ...item, desc: e.target.value } : item
                        );
                        setServicesData({ ...servicesData, detail: { ...servicesData.detail, items: updated } });
                      }}
                      rows={2}
                      className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white/80 text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-purple-400 uppercase block mb-1">Texto del Botón</label>
                      <input
                        type="text"
                        value={svc.buttonText || 'Reservar una Consulta'}
                        onChange={(e) => {
                          const updated = servicesData.detail.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, buttonText: e.target.value } : item
                          );
                          setServicesData({ ...servicesData, detail: { ...servicesData.detail, items: updated } });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-cyan-400 uppercase block mb-1">Enlace del Botón</label>
                      <input
                        type="text"
                        value={svc.buttonLink || '/contact'}
                        onChange={(e) => {
                          const updated = servicesData.detail.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, buttonLink: e.target.value } : item
                          );
                          setServicesData({ ...servicesData, detail: { ...servicesData.detail, items: updated } });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => handleSavePage('services')}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-purple-600 hover:opacity-90 disabled:opacity-50 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-cyan-600/30 cursor-pointer"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Guardar Cambios de Servicios ({cmsLang.toUpperCase()})
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 3: CONSULTING PAGE EDITOR ── */}
      {activeTab === 'consulting' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-slate-800/40 p-4 rounded-xl border border-purple-500/20">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse" />
              <span className="text-white font-bold text-sm">Editando: Consultoría de Negocios ({cmsLang === 'en' ? 'Inglés - EN' : 'Español - ES'})</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/business-consulting"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <ExternalLink size={14} /> Ver en Vivo
              </a>
              <button
                onClick={() => handleSavePage('consulting')}
                disabled={saving}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 cursor-pointer"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Guardar Consultoría ({cmsLang.toUpperCase()})
              </button>
            </div>
          </div>

          {/* Hero Consulting */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-purple-300">1. Hero de Consultoría</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Badge</label>
                <input
                  type="text"
                  value={consultingData.hero.badge}
                  onChange={(e) => setConsultingData({ ...consultingData, hero: { ...consultingData.hero, badge: e.target.value } })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Prefijo de Título</label>
                <input
                  type="text"
                  value={consultingData.hero.titlePrefix}
                  onChange={(e) => setConsultingData({ ...consultingData, hero: { ...consultingData.hero, titlePrefix: e.target.value } })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-purple-400 font-semibold block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={consultingData.hero.titleHighlight}
                  onChange={(e) => setConsultingData({ ...consultingData, hero: { ...consultingData.hero, titleHighlight: e.target.value } })}
                  className="w-full bg-slate-900 border border-purple-500/50 rounded-lg px-3 py-2 text-purple-300 text-sm font-bold"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 font-semibold block mb-1">Subtítulo</label>
              <textarea
                value={consultingData.hero.subtitle}
                onChange={(e) => setConsultingData({ ...consultingData, hero: { ...consultingData.hero, subtitle: e.target.value } })}
                rows={2}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>

          {/* Consulting Services with Add / Delete */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <i className="ri-building-line text-purple-400" /> 2. Servicios de Consultoría ({consultingData.services.items.length})
              </h3>
              <button
                type="button"
                onClick={() => {
                  const newService = {
                    icon: 'ri-building-2-line',
                    title: 'Nuevo Servicio de Consultoría',
                    color: '#8c52ff',
                    image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=700&h=450&fit=crop',
                    desc: 'Descripción detallada del servicio.',
                    benefits: ['Beneficio 1', 'Beneficio 2', 'Beneficio 3'],
                    forWho: 'Emprendedores y empresas.',
                    buttonText: 'Reservar una Consulta',
                    buttonLink: '/contact',
                  };
                  setConsultingData({
                    ...consultingData,
                    services: {
                      ...consultingData.services,
                      items: [...consultingData.services.items, newService],
                    },
                  });
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-lg shadow-purple-600/30"
              >
                <Plus size={16} /> Añadir Servicio de Consultoría
              </button>
            </div>

            <div className="space-y-4">
              {consultingData.services.items.map((svc: any, i: number) => (
                <div key={i} className="bg-slate-900/60 p-4 rounded-xl border border-slate-700/60 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">
                      Servicio #{i + 1}: {svc.title}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = consultingData.services.items.filter((_: any, idx: number) => idx !== i);
                        setConsultingData({
                          ...consultingData,
                          services: { ...consultingData.services, items: updated },
                        });
                      }}
                      className="px-2.5 py-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg text-xs font-medium flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 size={12} /> Eliminar
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-slate-400 uppercase block mb-1">Título</label>
                      <input
                        type="text"
                        value={svc.title}
                        onChange={(e) => {
                          const updated = consultingData.services.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, title: e.target.value } : item
                          );
                          setConsultingData({
                            ...consultingData,
                            services: { ...consultingData.services, items: updated },
                          });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400 uppercase block mb-1">Para quién es</label>
                      <input
                        type="text"
                        value={svc.forWho}
                        onChange={(e) => {
                          const updated = consultingData.services.items.map((item: any, idx: number) =>
                            idx === i ? { ...item, forWho: e.target.value } : item
                          );
                          setConsultingData({
                            ...consultingData,
                            services: { ...consultingData.services, items: updated },
                          });
                        }}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1 text-white text-xs"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] text-slate-400 uppercase block mb-1">Descripción</label>
                    <textarea
                      value={svc.desc}
                      onChange={(e) => {
                        const updated = consultingData.services.items.map((item: any, idx: number) =>
                          idx === i ? { ...item, desc: e.target.value } : item
                        );
                        setConsultingData({
                          ...consultingData,
                          services: { ...consultingData.services, items: updated },
                        });
                      }}
                      rows={2}
                      className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white/80 text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => handleSavePage('consulting')}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 hover:opacity-90 disabled:opacity-50 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-purple-600/30 cursor-pointer"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Guardar Cambios de Consultoría ({cmsLang.toUpperCase()})
            </button>
          </div>
        </div>
      )}

      {/* ── TAB 4: CONTACT PAGE EDITOR ── */}
      {activeTab === 'contact' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-slate-800/40 p-4 rounded-xl border border-pink-500/20">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-pink-400 animate-pulse" />
              <span className="text-white font-bold text-sm">Editando: Página de Contacto ({cmsLang === 'en' ? 'Inglés - EN' : 'Español - ES'})</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <ExternalLink size={14} /> Ver en Vivo
              </a>
              <button
                onClick={() => handleSavePage('contact')}
                disabled={saving}
                className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg shadow-emerald-600/30 cursor-pointer"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Guardar Contacto ({cmsLang.toUpperCase()})
              </button>
            </div>
          </div>

          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-pink-300">Datos de Contacto Directo</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Correo Electrónico</label>
                <input
                  type="email"
                  value={contactData.email}
                  onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Tiempo de Respuesta</label>
                <input
                  type="text"
                  value={contactData.response.value}
                  onChange={(e) =>
                    setContactData({
                      ...contactData,
                      response: { ...contactData.response, value: e.target.value },
                    })
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Área de Servicio</label>
                <input
                  type="text"
                  value={contactData.service.value}
                  onChange={(e) =>
                    setContactData({
                      ...contactData,
                      service: { ...contactData.service, value: e.target.value },
                    })
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-purple-300">Sección de Reserva de Calendario</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 font-semibold block mb-1">Título Destacado</label>
                <input
                  type="text"
                  value={contactData.calendar.titleHighlight}
                  onChange={(e) =>
                    setContactData({
                      ...contactData,
                      calendar: { ...contactData.calendar, titleHighlight: e.target.value },
                    })
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-purple-400 font-semibold block mb-1">Enlace de Agenda (Stan Store / Calendly)</label>
                <input
                  type="text"
                  value={contactData.calendar.link}
                  onChange={(e) =>
                    setContactData({
                      ...contactData,
                      calendar: { ...contactData.calendar, link: e.target.value },
                    })
                  }
                  className="w-full bg-slate-900 border border-purple-500/50 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => handleSavePage('contact')}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:opacity-90 disabled:opacity-50 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-pink-600/30 cursor-pointer"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Guardar Cambios de Contacto ({cmsLang.toUpperCase()})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSManager;
